import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingCart, User, Menu, X, MapPin, Search, Flame, Phone, Store, Truck, ShieldCheck, Ghost } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { STORE_ADDRESS, STORE_PHONE, STORE_PHONE_TEL, STORE_HOURS, STORE_GMAPS_DIRECTIONS_URL, SERVICE_AREA_LONG, GOOGLE_REVIEW_URL } from "@/constants/store";
import SupportChat from "@/components/SupportChat";
import CartDrawer from "@/components/shop/CartDrawer";

const NAV = [
  { label: "Shop All", to: "/shop" },
  { label: "Deals", to: "/deals", highlight: true },
  { label: "Halloween", to: "/halloween", spooky: true },
  { label: "Pallets", to: "/pallets", gold: true },
  { label: "Build a Pallet", to: "/build-pallet", gold: true },
  { label: "Recliners", to: "/shop/Recliners", cat: "Recliners" },
  { label: "Furniture", to: "/shop/Furniture", cat: "Furniture" },
  { label: "Scooters", to: "/shop/Scooters", cat: "Scooters" },
  { label: "E-Bikes", to: "/shop/E-Bikes", cat: "E-Bikes" },
  { label: "Electronics", to: "/shop/Electronics", cat: "Electronics" },
  { label: "Tools & Hardware", to: "/shop/Tools & Hardware", cat: "Tools & Hardware" },
  { label: "Appliances", to: "/shop/Appliances", cat: "Appliances" },
  { label: "Landscaping", to: "/shop/Landscaping", cat: "Landscaping" },
  { label: "Deals Under $20", to: "/deals-under-20", highlight: true },
  { label: "Closeout & Overstock", to: "/shop/Closeout & Overstock Pallets", cat: "Closeout & Overstock Pallets" },
];

const ANNOUNCEMENTS = [
  "Free same-day pickup in store — ready during store hours",
  "FREE local delivery on orders $299+ within 10 miles",
  "Ships to you — carrier shipping on hundreds of items",
  "New inventory lands on the floor every week",
];

function TopBar() {
  const [msg, setMsg] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsg((m) => (m + 1) % ANNOUNCEMENTS.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="bg-[#0A1C40] text-white text-xs" data-testid="topbar">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 tracking-wider uppercase font-medium min-w-0">
          <MapPin className="w-3 h-3 text-[#FFC220] shrink-0" />
          <span className="hidden sm:inline truncate">{STORE_ADDRESS.short} · </span>
          <span key={msg} className="truncate animate-fade-up" data-testid="topbar-announcement">
            {ANNOUNCEMENTS[msg]}
          </span>
        </div>
        <a
          href={`tel:${STORE_PHONE_TEL}`}
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FFC220] text-[#0A1C40] font-black px-3 py-1 rounded-full hover:bg-white transition-colors"
          data-testid="topbar-call-banner"
        >
          <Phone className="w-3 h-3" /> Questions? Call us: (616) 410-0158
        </a>
        <div className="hidden md:flex items-center gap-5 tracking-wider uppercase shrink-0">
          <Link to="/track" className="hover:text-[#FFC220] transition-colors" data-testid="topbar-track-order">Track Order</Link>
        </div>
      </div>
    </div>
  );
}

function SearchBar({ className = "" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop");
  };
  return (
    <form onSubmit={submit} className={`relative ${className}`} data-testid="header-search-form">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search recliners, pallets, e-bikes…"
        className="w-full bg-gray-100 border border-gray-200 rounded-full pl-5 pr-12 py-2.5 text-base md:text-sm outline-none focus:border-[#0A1C40] focus:bg-white transition-colors"
        data-testid="header-search-input"
      />
      <button
        type="submit"
        className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#FFC220] hover:bg-[#e6ae1c] flex items-center justify-center transition-colors"
        data-testid="header-search-btn"
        aria-label="Search"
      >
        <Search className="w-4 h-4 text-[#0A1C40]" />
      </button>
    </form>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [liveCats, setLiveCats] = useState(null);
  const { pathname } = useLocation();
  const callLift = pathname.startsWith("/build-pallet") ? "bottom-[210px]" : "bottom-[152px]";
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/products/categories").then((r) => setLiveCats(r.data.categories || [])).catch(() => setLiveCats(null));
  }, []);

  // Empty departments disappear automatically (Fix #2/#9) — non-category links always show
  const nav = NAV.filter((n) => !n.cat || liveCats === null || liveCats.includes(n.cat));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <TopBar />
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-200"
        data-testid="main-header"
      >
        {/* Main row: logo · search · account/cart */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between gap-3 md:gap-6 h-16 md:h-[72px]">
            <div className="flex items-center gap-1 shrink-0">
              <button
                className="md:hidden p-2 -ml-2"
                onClick={() => setOpen(!open)}
                data-testid="mobile-menu-btn"
              >
                {open ? <X /> : <Menu />}
              </button>
              <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
                <img src="/logo-mark.png" alt="" className="h-8 w-auto" />
                <div className="font-display font-black text-xl tracking-tight text-[#0A1C40] leading-none whitespace-nowrap">
                  IPVALUE
                  <div className="text-[9px] tracking-[0.28em] text-[#C99700] font-bold mt-0.5">
                    MARKETPLACE
                  </div>
                </div>
              </Link>
            </div>

            <SearchBar className="hidden md:block flex-1 max-w-2xl" />

            <div className="flex items-center gap-1 md:gap-4 shrink-0">
              {user ? (
                <Link
                  to={user.role === "admin" ? "/admin" : "/account"}
                  className="p-2 hover:text-[#0A1C40] flex flex-col items-center leading-none gap-0.5"
                  data-testid="account-link"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline text-[11px] font-semibold">
                    {user.role === "admin" ? "Admin" : user.name?.split(" ")[0]}
                  </span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="p-2 hover:text-[#0A1C40] flex flex-col items-center leading-none gap-0.5"
                  data-testid="login-link"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline text-[11px] font-semibold">Sign In</span>
                </Link>
              )}
              <button
                onClick={() => setDrawerOpen(true)}
                className="relative p-2 hover:text-[#0A1C40] flex flex-col items-center leading-none gap-0.5"
                data-testid="cart-link"
              >
                <div className="relative">
                  <ShoppingCart className="w-5 h-5" />
                  {count > 0 && (
                    <span
                      className="absolute -top-2 -right-2.5 bg-[#FFC220] text-[#0A1C40] text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center"
                      data-testid="cart-count-badge"
                    >
                      {count}
                    </span>
                  )}
                </div>
                <span className="hidden md:inline text-[11px] font-semibold">Cart</span>
              </button>
            </div>
          </div>

          {/* Mobile search row */}
          <div className="md:hidden pb-3">
            <SearchBar />
          </div>
        </div>

        {/* Department sub-nav — scrollable strip on mobile, full row on desktop */}
        <nav className="border-t border-gray-100 bg-white" data-testid="dept-subnav">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center gap-4 md:gap-6 overflow-x-auto py-2.5 text-sm font-semibold [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Link
              to="/departments"
              className="whitespace-nowrap shrink-0 inline-flex items-center gap-1 text-[#0A1C40] font-bold border border-[#0A1C40] rounded-full px-3 py-0.5 hover:bg-[#0A1C40] hover:text-[#FFC220] transition-colors"
              data-testid="nav-all-departments"
            >
              All Departments
            </Link>
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className={`whitespace-nowrap shrink-0 transition-colors ${
                  n.highlight
                    ? "inline-flex items-center gap-1 text-[#DC2626] hover:text-[#b91c1c] font-bold"
                    : n.spooky
                    ? "inline-flex items-center gap-1 text-[#EA580C] hover:text-[#c2410c] font-bold"
                    : n.gold
                    ? "text-[#C99700] hover:text-[#a87e00] font-bold"
                    : "text-gray-700 hover:text-[#0A1C40]"
                }`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.highlight && <Flame className="w-3.5 h-3.5" />}
                {n.spooky && <Ghost className="w-3.5 h-3.5" />}
                {n.label}
              </Link>
            ))}
          </div>
        </nav>

        {open && (
          <nav className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-1">
              <Link
                to="/departments"
                onClick={() => setOpen(false)}
                className="py-2 font-black text-[#0A1C40]"
                data-testid="mobile-nav-all-departments"
              >
                All Departments →
              </Link>
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={`py-2.5 text-sm font-semibold ${
                    n.highlight ? "text-[#DC2626]" : n.spooky ? "text-[#EA580C]" : n.gold ? "text-[#C99700]" : ""
                  }`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <SupportChat />
      <CartDrawer />

      {/* Addendum C — mobile floating tap-to-call. Positioned above the chat widget
          and clear of sticky bottom bars so it never covers prices or buttons. */}
      <a
        href={`tel:${STORE_PHONE_TEL}`}
        className={`md:hidden fixed ${callLift} right-4 z-30 w-11 h-11 rounded-full bg-[#0A1C40] border-2 border-[#FFC220] phone-ring flex items-center justify-center shadow-xl [touch-action:manipulation]`}
        data-testid="floating-call-btn"
        aria-label="Call the store"
      >
        <Phone className="w-5 h-5 text-[#FFC220]" />
      </a>

      <footer className="bg-[#0A1C40] text-white mt-20" data-testid="main-footer">
        {/* Footer trust row */}
        <div className="border-b border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-[#FFC220] shrink-0" />
              <span className="font-semibold">FREE delivery on orders $299+ within 10 miles</span>
            </div>
            <div className="flex items-center gap-3">
              <Store className="w-5 h-5 text-[#FFC220] shrink-0" />
              <span className="font-semibold">Free pickup — ready during store hours</span>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-[#FFC220] shrink-0" />
              <span className="font-semibold">Secure checkout powered by Square</span>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src="/logo.png" alt="IPVALUE MARKETPLACE" className="h-24 w-auto" />
            <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-md">
              Closeout, overstock &amp; liquidation retailer. Serving {SERVICE_AREA_LONG} from
              our Grand Rapids, MI showroom. Recliners, furniture, mattresses, e-bikes,
              appliances, landscaping equipment &amp; wholesale closeout pallets. Local
              pickup or delivery.
            </p>
            <div className="mt-8 ipv-eyebrow text-[#FFC220]/70">Showroom</div>
            <div className="mt-2 text-sm text-white/80" data-testid="footer-address">
              {STORE_ADDRESS.street}<br />
              {STORE_ADDRESS.city}, {STORE_ADDRESS.state} {STORE_ADDRESS.postal}<br />
              <a href={`tel:${STORE_PHONE_TEL}`} className="text-[#FFC220] hover:text-white transition-colors" data-testid="footer-phone">
                {STORE_PHONE}
              </a>
            </div>
            <a
              href={STORE_GMAPS_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest bg-[#FFC220] text-[#0A1C40] px-3 py-1.5 hover:bg-white transition-colors"
              data-testid="footer-gmaps-directions"
            >
              <MapPin className="w-3 h-3" /> Get directions
            </a>
            <div className="mt-4 ipv-eyebrow text-[#FFC220]/70">Store hours</div>
            <div className="mt-2 text-sm text-white/80 space-y-0.5" data-testid="footer-hours">
              {STORE_HOURS.displayLines.map((row) => (
                <div key={row.days}><span className="text-white/50 w-20 inline-block">{row.days}</span> {row.hours}</div>
              ))}
            </div>
          </div>
          <div>
            <div className="ipv-eyebrow text-[#FFC220]/70">Shop</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li><Link to="/shop" className="hover:text-[#FFC220] transition-colors">All Products</Link></li>
              <li><Link to="/deals" className="hover:text-[#FFC220] transition-colors">Hot Deals</Link></li>
              <li><Link to="/pallets" className="hover:text-[#FFC220] transition-colors">Pallet Sales</Link></li>
              <li><Link to="/deals-under-20" className="hover:text-[#FFC220] transition-colors">Deals Under $20</Link></li>
              <li><Link to="/shop/Closeout & Overstock Pallets" className="hover:text-[#FFC220] transition-colors">Closeout & Overstock Pallets</Link></li>
              <li><Link to="/shop/Furniture" className="hover:text-[#FFC220] transition-colors">Furniture</Link></li>
              <li><Link to="/shop/E-Bikes" className="hover:text-[#FFC220] transition-colors">E-Bikes</Link></li>
              <li><Link to="/build-pallet" className="text-[#FFC220] hover:text-white transition-colors font-bold" data-testid="footer-build-pallet-link">Build Your Own Pallet</Link></li>
            </ul>
          </div>
          <div>
            <div className="ipv-eyebrow text-[#FFC220]/70">Help &amp; Policies</div>
            <ul className="mt-4 space-y-2 text-sm text-white/80">
              <li><Link to="/track" className="hover:text-[#FFC220] transition-colors">Track Order</Link></li>
              <li><Link to="/account" className="hover:text-[#FFC220] transition-colors">My Account</Link></li>
              <li><Link to="/about" className="hover:text-[#FFC220] transition-colors" data-testid="footer-about-link">How We Buy, How We Price</Link></li>
              <li><Link to="/resellers" className="hover:text-[#FFC220] transition-colors" data-testid="footer-resellers-link">Reseller Program</Link></li>
              <li><Link to="/policies/delivery" className="hover:text-[#FFC220] transition-colors" data-testid="footer-delivery-policy">Delivery &amp; Pickup</Link></li>
              <li><Link to="/policies/returns" className="hover:text-[#FFC220] transition-colors" data-testid="footer-return-policy">Return &amp; Exchange</Link></li>
              <li><Link to="/policies/pallets" className="hover:text-[#FFC220] transition-colors" data-testid="footer-pallet-policy">Pallet Guarantee &amp; Terms</Link></li>
              <li><Link to="/privacy" className="hover:text-[#FFC220] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-[#FFC220] transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer" className="text-[#FFC220] hover:text-white transition-colors font-bold" data-testid="footer-google-review-link">★ Review us on Google</a></li>
              <li><Link to="/login" className="hover:text-[#FFC220] transition-colors">Sign In</Link></li>
              <li>
                <Link to="/sales-rep/signup" className="hover:text-[#FFC220] transition-colors inline-flex items-center gap-1" data-testid="footer-sales-rep-link">
                  Earn 10% · Become a Rep
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-6 text-xs text-white/40 flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} IPVALUE MARKETPLACE LLC — All Rights Reserved</div>
            <div className="flex items-center gap-4">
              {/* Discreet age-restricted entries (Fix #13) — reachable, never promoted */}
              <Link to="/adult" className="text-white/25 hover:text-white/60 transition-colors" data-testid="footer-adult-link">Adult Wellness (18+)</Link>
              <Link to="/tobacco" className="text-white/25 hover:text-white/60 transition-colors" data-testid="footer-tobacco-link">Tobacco Accessories (21+)</Link>
              <span>Secure payments by Square</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
